export const FEED_JUMP_START_MARK = 'feed:jump:start';
export const FEED_JUMP_COMPLETE_MARK = 'feed:jump:complete';
export const FEED_BOOT_START_MARK = 'feed:boot:start';
export const FEED_BOOT_FIRST_WINDOW_MARK = 'feed:boot:first-window';
export const FEED_RESTORE_ANCHORED_MARK = 'feed:restore:anchored';
export const FEED_JUMP_MEASURE = 'feed:jump';

export const FEED_PERFORMANCE_BOOT_START_EVENT = 'performance:boot:start';
export const FEED_PERFORMANCE_BOOT_FIRST_WINDOW_EVENT = 'performance:boot:first-window';
export const FEED_PERFORMANCE_RESTORE_ANCHORED_EVENT = 'performance:restore:anchored';

type FeedPerformanceMarkName =
  | typeof FEED_JUMP_START_MARK
  | typeof FEED_JUMP_COMPLETE_MARK
  | typeof FEED_BOOT_START_MARK
  | typeof FEED_BOOT_FIRST_WINDOW_MARK
  | typeof FEED_RESTORE_ANCHORED_MARK;

const SESSION_EVENT_MARKS = new Map<string, FeedPerformanceMarkName>([
  [FEED_PERFORMANCE_BOOT_START_EVENT, FEED_BOOT_START_MARK],
  [FEED_PERFORMANCE_BOOT_FIRST_WINDOW_EVENT, FEED_BOOT_FIRST_WINDOW_MARK],
  [FEED_PERFORMANCE_RESTORE_ANCHORED_EVENT, FEED_RESTORE_ANCHORED_MARK]
]);

/** Records a feed-scoped performance mark when the browser Performance API is available. */
export function markFeedPerformance(name: FeedPerformanceMarkName): void {
  const api = performanceApi();
  if (!api || typeof api.mark !== 'function') return;

  try {
    api.mark(name);
  } catch {
    // Performance marks are diagnostic only.
  }
}

/** Records a feed-scoped measure between two existing marks when supported. */
export function measureFeedPerformance(
  name: typeof FEED_JUMP_MEASURE,
  startMark: FeedPerformanceMarkName,
  endMark: FeedPerformanceMarkName
): void {
  const api = performanceApi();
  if (!api || typeof api.measure !== 'function') return;

  try {
    api.measure(name, startMark, endMark);
  } catch {
    // Missing or cleared marks should not affect feed behavior.
  }
}

/** Marks a jump request trigger in the UI message-jump path. */
export function markFeedJumpStart(): void {
  markFeedPerformance(FEED_JUMP_START_MARK);
}

/** Marks the highlighted jump landing and records the feed:jump duration. */
export function markFeedJumpComplete(): void {
  markFeedPerformance(FEED_JUMP_COMPLETE_MARK);
  measureFeedPerformance(FEED_JUMP_MEASURE, FEED_JUMP_START_MARK, FEED_JUMP_COMPLETE_MARK);
}

/** Converts feed session debug events into permanent UI performance marks. */
export function markFeedSessionPerformanceEvent(kind: string): boolean {
  const markName = SESSION_EVENT_MARKS.get(kind);
  if (!markName) return false;

  markFeedPerformance(markName);
  return true;
}

function performanceApi(): Performance | undefined {
  if (typeof globalThis === 'undefined') return undefined;
  return globalThis.performance;
}
